import { useEffect, useRef, useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { getDeptSession, clearDeptSession } from "../lib/deptAuth";

const BASE_URL = (import.meta as any).env?.VITE_API_URL ?? "";

// ── types ─────────────────────────────────────────────────────────────────────

interface Thread {
  id: string;
  vessel_id: string;
  contact_id: string;
  subject: string;
  created_at: string;
  updated_at: string;
  vessel_name: string | null;
  vessel_imo: string | null;
  last_body: string | null;
  last_sender_type: "vessel" | "shore" | null;
  last_message_at: string | null;
  unread_count: number;
}

interface Message {
  id: string;
  thread_id: string;
  sender_type: "vessel" | "shore";
  sender_name: string | null;
  sender_id: string | null;
  body: string;
  acknowledged_at: string | null;
  created_at: string;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

async function deptFetch<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── DeptPortal ────────────────────────────────────────────────────────────────

export default function DeptPortal() {
  const navigate = useNavigate();
  const session = getDeptSession();

  useEffect(() => {
    if (!session) navigate("/dept/login", { replace: true });
  }, [session, navigate]);

  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [threadMsgs, setThreadMsgs] = useState<Message[]>([]);
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  function loadThreads() {
    if (!session) return;
    deptFetch<Thread[]>(session.token, "/api/dept/messages")
      .then(setThreads)
      .catch(console.error);
  }

  useEffect(loadThreads, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [threadMsgs]);

  if (!session) return null;

  async function selectThread(thread: Thread) {
    setSelectedThread(thread);
    setThreadMsgs([]);
    setReplyBody("");
    setMobileView("thread");
    try {
      const data = await deptFetch<{ thread: Thread; messages: Message[] }>(
        session!.token,
        `/api/dept/messages/${thread.id}`
      );
      setThreadMsgs(data.messages);
      setThreads((ts) =>
        ts.map((t) => (t.id === thread.id ? { ...t, unread_count: 0 } : t))
      );
    } catch (e) {
      console.error(e);
    }
  }

  async function sendReply(e: FormEvent) {
    e.preventDefault();
    if (!selectedThread || !replyBody.trim()) return;
    setReplySending(true);
    try {
      await deptFetch(session!.token, `/api/dept/messages/${selectedThread.id}/reply`, {
        method: "POST",
        body: JSON.stringify({ body: replyBody.trim() }),
      });
      const data = await deptFetch<{ thread: Thread; messages: Message[] }>(
        session!.token,
        `/api/dept/messages/${selectedThread.id}`
      );
      setThreadMsgs(data.messages);
      setReplyBody("");
      loadThreads();
    } catch (e) {
      console.error(e);
    } finally {
      setReplySending(false);
    }
  }

  async function acknowledge(msgId: string) {
    try {
      await deptFetch(session!.token, `/api/dept/messages/${msgId}/acknowledge`, {
        method: "POST",
      });
      setThreadMsgs((ms) =>
        ms.map((m) =>
          m.id === msgId ? { ...m, acknowledged_at: new Date().toISOString() } : m
        )
      );
      loadThreads();
    } catch (e) {
      console.error(e);
    }
  }

  function signOut() {
    clearDeptSession();
    navigate("/dept/login");
  }

  const displayName = session.role_name ?? session.name;

  return (
    <div className="flex flex-col h-screen bg-gray-50">

      {/* ── Top bar ──────────────────────────────────────────────── */}
      <header className="shrink-0 bg-[#0f1f3d] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold tracking-widest text-blue-300 uppercase">SeaVault</span>
          <span className="text-white/30">|</span>
          <div>
            <span className="font-semibold text-sm">{displayName}</span>
            <span className="ml-2 text-xs text-blue-200">Department Portal</span>
          </div>
        </div>
        <button
          onClick={signOut}
          className="text-xs text-blue-300 hover:text-white transition-colors"
        >
          Sign out
        </button>
      </header>

      {/* ── Messages panel ───────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Thread list */}
        <aside
          className={`${
            mobileView === "thread" ? "hidden" : "flex"
          } md:flex flex-col w-full md:w-80 shrink-0 border-r bg-white overflow-hidden`}
        >
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Vessel Messages
            </p>
          </div>

          <div className="flex-1 overflow-y-auto divide-y">
            {threads.length === 0 && (
              <p className="text-center text-sm text-gray-400 py-12">No messages yet</p>
            )}
            {threads.map((thread) => (
              <button
                key={thread.id}
                onClick={() => selectThread(thread)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  selectedThread?.id === thread.id ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {thread.unread_count > 0 && (
                        <span className="shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                      )}
                      <span className="font-medium text-sm text-gray-900 truncate">
                        {thread.vessel_name ?? "Vessel"}
                      </span>
                      {thread.vessel_imo && (
                        <span className="shrink-0 text-xs text-gray-400">IMO {thread.vessel_imo}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">{thread.subject}</p>
                    {thread.last_body && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {thread.last_sender_type === "shore" ? "You: " : ""}
                        {thread.last_body.slice(0, 60)}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    {thread.last_message_at && (
                      <span className="text-xs text-gray-400">{formatTime(thread.last_message_at)}</span>
                    )}
                    {thread.unread_count > 0 && (
                      <span className="block mt-1 ml-auto w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                        {thread.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Thread view */}
        <main
          className={`${
            mobileView === "list" ? "hidden" : "flex"
          } md:flex flex-col flex-1 overflow-hidden`}
        >
          {selectedThread ? (
            <>
              {/* Thread header */}
              <div className="shrink-0 bg-white border-b px-4 py-3 flex items-center gap-3">
                <button
                  onClick={() => setMobileView("list")}
                  className="md:hidden text-gray-500 hover:text-gray-800 text-lg leading-none"
                  aria-label="Back"
                >
                  ←
                </button>
                <div>
                  <p className="font-semibold text-sm text-gray-900">
                    {selectedThread.vessel_name ?? "Vessel"}
                    {selectedThread.vessel_imo && (
                      <span className="ml-2 text-xs font-normal text-gray-400">
                        IMO {selectedThread.vessel_imo}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">{selectedThread.subject}</p>
                </div>
              </div>

              {/* Message bubbles */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {threadMsgs.map((msg) => {
                  const isShore = msg.sender_type === "shore";
                  return (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${isShore ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`max-w-xs sm:max-w-sm rounded-2xl px-4 py-2.5 text-sm ${
                          isShore
                            ? "bg-[#1d5199] text-white rounded-br-sm"
                            : "bg-gray-100 text-gray-800 rounded-bl-sm"
                        }`}
                      >
                        {msg.body}
                      </div>
                      <div className="flex items-center gap-2 mt-1 px-1">
                        <span className="text-xs text-gray-400">
                          {msg.sender_name ?? (isShore ? "Shore" : "Vessel")} · {formatTime(msg.created_at)}
                        </span>
                        {!isShore && !msg.acknowledged_at && (
                          <button
                            onClick={() => acknowledge(msg.id)}
                            className="text-xs text-[#1d5199] hover:underline font-medium"
                          >
                            Acknowledge
                          </button>
                        )}
                        {!isShore && msg.acknowledged_at && (
                          <span className="text-xs text-gray-400">✓ Acknowledged</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              <form
                onSubmit={sendReply}
                className="shrink-0 border-t bg-white p-3 flex gap-2 items-end"
              >
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type a reply…"
                  rows={2}
                  className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (replyBody.trim()) sendReply(e as any);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={replySending || !replyBody.trim()}
                  className="shrink-0 rounded-lg bg-[#1d5199] text-white px-4 py-2 text-sm font-semibold hover:bg-[#174680] disabled:opacity-50 transition-colors"
                >
                  {replySending ? "…" : "Send"}
                </button>
              </form>
            </>
          ) : (
            <div className="hidden md:flex flex-1 items-center justify-center text-gray-400 text-sm">
              Select a conversation
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
